import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  TrendingUp, Globe, Zap, ArrowRight, CheckCircle2,
  DollarSign, BarChart3, Mail,
  ChevronDown, Shield, Rocket, Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEOHead from '@/components/SEOHead';

// ─── Data ─────────────────────────────────────────────────────────────────────

const STATS = [
  { value: '10K+', label: 'Websites Generated', icon: Globe },
  { value: '$2.4B', label: 'Addressable Market', icon: TrendingUp },
  { value: '4.8★', label: 'Avg User Rating', icon: Star },
  { value: '3min', label: 'Avg Time to Deploy', icon: Zap },
];

const HIGHLIGHTS = [
  {
    icon: Rocket,
    title: 'First-Mover Advantage',
    body: 'AI-native website builder in a market dominated by legacy drag-and-drop tools. We ship what others are still whiteboarding.',
  },
  {
    icon: TrendingUp,
    title: 'Explosive Growth Trajectory',
    body: 'Month-over-month user growth powered entirely by word-of-mouth and organic search — zero paid acquisition spend to date.',
  },
  {
    icon: Shield,
    title: 'Defensible Moat',
    body: 'Proprietary prompt engineering, fine-tuned generation pipelines, and a flywheel of user data that improves output quality with every generation.',
  },
  {
    icon: DollarSign,
    title: 'Clear Monetisation Path',
    body: 'Subscription tiers already live with paying customers. Roadmap includes white-label licensing, agency plans, and API access.',
  },
];

const INVESTMENT_RANGES = [
  'Please select…',
  '$10,000 – $50,000',
  '$50,000 – $250,000',
  '$250,000 – $1,000,000',
  '$1,000,000+',
  'Open to discussion',
];

const INVESTOR_TYPES = [
  'Please select…',
  'Angel Investor',
  'Venture Capital',
  'Family Office',
  'Strategic / Corporate',
  'Accelerator / Incubator',
  'Other',
];

const EMPTY_FORM = {
  name: '',
  email: '',
  phone: '',
  company: '',
  investorType: '',
  investmentRange: '',
  geography: '',
  message: '',
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ value, label, icon: Icon, index }) {
  return (
    <div
     
     
     
     
      className="flex flex-col items-center gap-2 bg-white/[0.03] border border-white/8 rounded-2xl px-6 py-8 text-center hover:border-orange-500/30 transition-colors"
    >
      <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center mb-1">
        <Icon className="w-5 h-5 text-orange-400" />
      </div>
      <span className="text-3xl font-extrabold text-white tracking-tight">{value}</span>
      <span className="text-sm text-zinc-500">{label}</span>
    </div>
  );
}

function HighlightCard({ icon: Icon, title, body, index }) {
  return (
    <div
     
     
     
     
      className="group bg-white/[0.03] border border-white/8 rounded-2xl p-6 hover:border-orange-500/30 hover:bg-white/[0.05] transition-all"
    >
      <div className="w-11 h-11 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center mb-4 group-hover:bg-orange-500/15 transition-colors">
        <Icon className="w-5 h-5 text-orange-400" />
      </div>
      <h3 className="text-white font-semibold text-lg mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{body}</p>
    </div>
  );
}

function SelectField({ label, id, value, onChange, options, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <div className="relative">
        <select
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          required={required}
          className="w-full appearance-none bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all pr-10"
          style={{ colorScheme: 'dark' }}
        >
          {options.map(opt => (
            <option key={opt} value={opt === 'Please select…' ? '' : opt} className="bg-zinc-900">
              {opt}
            </option>
          ))}
        </select>
        <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
      </div>
    </div>
  );
}

function InputField({ label, id, type = 'text', value, onChange, placeholder, required }) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-medium text-zinc-300">
        {label}{required && <span className="text-orange-500 ml-1">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all"
      />
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function Invest() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (key) => (val) => setForm(f => ({ ...f, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          access_key: '00ed92cb-5032-4578-b9be-306700b2271e',
          subject: `Investor Enquiry — ${form.name} (${form.investorType || 'Unknown type'}) · ${form.investmentRange || 'Range TBD'}`,
          from_name: form.name,
          email: form.email,
          phone: form.phone || 'Not provided',
          company_or_fund: form.company || 'Not provided',
          investor_type: form.investorType || 'Not specified',
          investment_range: form.investmentRange || 'Not specified',
          geography: form.geography || 'Not provided',
          message: form.message || 'No message provided',
        }),
      });

      const data = await res.json();
      if (data.success) {
        setStatus('success');
        setForm(EMPTY_FORM);
      } else {
        throw new Error(data.message || 'Submission failed');
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err.message || 'Something went wrong. Please try again.');
      setTimeout(() => setStatus('idle'), 6000);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <SEOHead
        title="Invest in BuildersLab"
        description="Interested in investing in BuildersLab? Explore our growth story, traction metrics, and investment opportunities in AI-powered web development."
        ogUrl="https://builderslab.net/Invest"
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative pt-36 pb-24 overflow-hidden">
        {/* Ambient glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-orange-500/8 blur-[120px] rounded-full pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[300px] bg-purple-500/6 blur-[100px] rounded-full pointer-events-none" />

        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <div
           
           
           
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 text-sm text-orange-400 font-medium mb-6">
              Investor Relations
            </div>

            <h1 className="text-5xl sm:text-6xl font-extrabold leading-[1.1] tracking-tight mb-6">
              Build the Future of{' '}
              <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">
                Web Creation
              </span>{' '}
              With Us
            </h1>

            <p className="text-xl text-zinc-400 leading-relaxed max-w-2xl mx-auto mb-10">
              BuildersLab is redefining how the world builds for the web. We're raising a round
              to accelerate product development, grow our team, and capture the AI-native
              website builder market.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a
                href="#invest-form"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold shadow-[0_0_30px_rgba(249,115,22,0.3)] hover:shadow-[0_0_40px_rgba(249,115,22,0.45)] transition-all group"
              >
                Express Interest
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="mailto:info@builderslab.net"
                className="inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full bg-white/5 border border-white/10 hover:bg-white/10 text-white font-medium transition-all"
              >
                <Mail className="w-4 h-4" />
                info@builderslab.net
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────────── */}
      <section className="py-16 border-y border-white/5">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <StatCard key={s.label} {...s} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Invest ───────────────────────────────────────────────────── */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div
           
           
           
           
            className="text-center mb-14"
          >
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 tracking-tight">
              Why Invest in BuildersLab?
            </h2>
            <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
              The $50B website builder market is ripe for disruption. We're not building
              a better Wix — we're making Wix irrelevant.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5">
            {HIGHLIGHTS.map((h, i) => (
              <HighlightCard key={h.title} {...h} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ── Use of Funds ─────────────────────────────────────────────────── */}
      <section className="py-20 border-y border-white/5 bg-white/[0.015]">
        <div className="max-w-4xl mx-auto px-6">
          <div
           
           
           
           
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-extrabold mb-3 tracking-tight">Use of Funds</h2>
            <p className="text-zinc-400">How your investment accelerates growth</p>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { pct: '45%', label: 'Product & Engineering', desc: 'Expand the core AI engine, ship mobile apps, and build the agency platform.', color: 'from-orange-500 to-orange-400' },
              { pct: '30%', label: 'Growth & Marketing', desc: 'First paid acquisition campaigns, influencer partnerships, and SEO scale-up.', color: 'from-purple-500 to-purple-400' },
              { pct: '25%', label: 'Team & Operations', desc: 'Key hires in design, sales, and customer success to support 10× user growth.', color: 'from-blue-500 to-blue-400' },
            ].map((item, i) => (
              <div
                key={item.label}
               
               
               
               
                className="bg-white/[0.03] border border-white/8 rounded-2xl p-6 text-center"
              >
                <div className={`text-4xl font-extrabold bg-gradient-to-r ${item.color} bg-clip-text text-transparent mb-3`}>
                  {item.pct}
                </div>
                <div className="text-white font-semibold mb-2">{item.label}</div>
                <div className="text-zinc-500 text-sm leading-relaxed">{item.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Investor Form ─────────────────────────────────────────────────── */}
      <section id="invest-form" className="py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(249,115,22,0.05)_0%,transparent_65%)] pointer-events-none" />

        <div className="relative max-w-2xl mx-auto px-6">
          <div
           
           
           
           
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 text-sm text-orange-400 font-medium mb-5">
              <BarChart3 className="w-3.5 h-3.5" />
              Investment Enquiry
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4 tracking-tight">
              Let's Start a Conversation
            </h2>
            <p className="text-zinc-400 leading-relaxed">
              Complete the form and our founding team will respond within 48 hours with
              our pitch deck and a proposed meeting time.
            </p>
          </div>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <div
                key="success"
               
               
               
               
                className="bg-emerald-500/10 border border-emerald-500/25 rounded-3xl p-12 text-center"
              >
                <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-3">Enquiry Received!</h3>
                <p className="text-zinc-400 leading-relaxed max-w-sm mx-auto">
                  Thank you for your interest in BuildersLab. We'll be in touch within
                  48 hours with our pitch deck and next steps.
                </p>
                <div className="mt-8 flex items-center justify-center gap-2 text-sm text-zinc-500">
                  <Mail className="w-4 h-4" />
                  <span>Check your inbox for a confirmation</span>
                </div>
              </div>
            ) : (
              <div
                key="form"
               
               
               
              >
                {/* Card */}
                <div className="relative">
                  <div className="absolute -inset-[1px] rounded-3xl bg-gradient-to-br from-orange-500/20 via-transparent to-purple-500/15 blur-sm" />
                  <div className="relative bg-zinc-950 border border-white/10 rounded-3xl p-8 sm:p-10 shadow-2xl">

                    <form onSubmit={handleSubmit} className="space-y-5">
                      {/* Row 1 — Name + Email */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField
                          label="Full Name" id="name"
                          value={form.name} onChange={set('name')}
                          placeholder="John Doe" required
                        />
                        <InputField
                          label="Email Address" id="email" type="email"
                          value={form.email} onChange={set('email')}
                          placeholder="john@example.com" required
                        />
                      </div>

                      {/* Row 2 — Phone + Company */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <InputField
                          label="Phone Number" id="phone" type="tel"
                          value={form.phone} onChange={set('phone')}
                          placeholder="+1 (555) 000-0000"
                        />
                        <InputField
                          label="Company / Fund Name" id="company"
                          value={form.company} onChange={set('company')}
                          placeholder="Acme Ventures"
                        />
                      </div>

                      {/* Row 3 — Investor Type + Investment Range */}
                      <div className="grid sm:grid-cols-2 gap-4">
                        <SelectField
                          label="Investor Type" id="investorType"
                          value={form.investorType} onChange={set('investorType')}
                          options={INVESTOR_TYPES} required
                        />
                        <SelectField
                          label="Investment Range" id="investmentRange"
                          value={form.investmentRange} onChange={set('investmentRange')}
                          options={INVESTMENT_RANGES} required
                        />
                      </div>

                      {/* Geography */}
                      <InputField
                        label="Geographic Location" id="geography"
                        value={form.geography} onChange={set('geography')}
                        placeholder="e.g. San Francisco, CA · USA"
                      />

                      {/* Message */}
                      <div className="space-y-1.5">
                        <label htmlFor="message" className="block text-sm font-medium text-zinc-300">
                          What excites you about BuildersLab?
                        </label>
                        <textarea
                          id="message"
                          value={form.message}
                          onChange={e => set('message')(e.target.value)}
                          rows={4}
                          placeholder="Tell us about your investment thesis, portfolio synergies, or questions you have…"
                          className="w-full bg-zinc-900 border border-zinc-800 rounded-xl px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500/30 transition-all resize-none"
                        />
                      </div>

                      {/* Error */}
                      <AnimatePresence>
                        {status === 'error' && (
                          <p
                           
                           
                           
                            className="text-red-400 text-sm bg-red-500/10 border border-red-500/20 rounded-xl px-4 py-3"
                          >
                            {errorMsg}
                          </p>
                        )}
                      </AnimatePresence>

                      {/* Submit */}
                      <Button
                        type="submit"
                        disabled={status === 'loading'}
                        className="w-full h-13 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-400 hover:to-orange-500 text-white font-semibold rounded-xl shadow-[0_0_20px_rgba(249,115,22,0.25)] hover:shadow-[0_0_30px_rgba(249,115,22,0.4)] transition-all disabled:opacity-60 disabled:cursor-not-allowed group text-base"
                      >
                        {status === 'loading' ? (
                          <span className="flex items-center justify-center gap-2">
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            Sending Enquiry…
                          </span>
                        ) : (
                          <span className="flex items-center justify-center gap-2">
                            Submit Investment Enquiry
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </span>
                        )}
                      </Button>

                      <p className="text-center text-xs text-zinc-600">
                        Your information is kept strictly confidential. We respect your privacy.
                      </p>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ── Bottom CTA strip ─────────────────────────────────────────────── */}
      <section className="border-t border-white/5 py-16">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-zinc-500 text-sm mb-2">Prefer to reach us directly?</p>
          <a
            href="mailto:info@builderslab.net"
            className="inline-flex items-center gap-2 text-orange-400 hover:text-orange-300 font-semibold transition-colors text-lg"
          >
            <Mail className="w-5 h-5" />
            info@builderslab.net
          </a>
        </div>
      </section>

    </div>
  );
}
